import { useState } from "react"
import { modifiers } from "ui-tools"
import type { PeerProfile } from "../../types"
import s from "./PeerAvatar.module.scss"

/** 32 in a thread or a group cluster, 48 in the conversation list. */
type PeerAvatarSize = "small" | "medium" | "large"

type PeerAvatarProps = PeerProfile & {
  size: PeerAvatarSize
}

/**
 * One person, by picture or by initial.
 *
 * Chat has its own rather than using the shared `Avatar` because the two want
 * different things from the same asset: that one crops to a circle, which
 * suits the square photos elsewhere, while chat shows the whole 2:3 portrait
 * illustration the design draws.
 */
export function PeerAvatar({ displayName, avatar, size }: PeerAvatarProps) {
  // A broken URL falls back like a missing one, rather than a broken-image icon.
  const [failed, setFailed] = useState(false)

  if (!avatar || failed) {
    return (
      <div
        className={modifiers(s, "initial", size)}
        // Same role as the image below, so either way it reads as a person.
        role="img"
        aria-label={displayName}
      >
        {/* Spread, so a name starting with an emoji is not cut in half. */}
        {[...displayName][0]}
      </div>
    )
  }

  return (
    <img
      className={modifiers(s, "peerAvatar", size)}
      src={avatar}
      alt={displayName}
      onError={() => setFailed(true)}
    />
  )
}

type PeerAvatarGroupProps = {
  members: PeerProfile[]
  size: PeerAvatarSize
}

const MAX_SHOWN = 3

/**
 * A group, as the faces of its first few members. Not a count — the member
 * count has its place in the thread header, where the design puts it.
 */
export function PeerAvatarGroup({ members, size }: PeerAvatarGroupProps) {
  return (
    <div className={modifiers(s, "group", size)}>
      {members.slice(0, MAX_SHOWN).map((member, index) => (
        <PeerAvatar
          // biome-ignore lint/suspicious/noArrayIndexKey: a short static slice that never reorders
          key={index}
          {...member}
          size={size}
        />
      ))}
    </div>
  )
}
